import { useRef, useState } from "react";
import type { ProjectType } from "@vorloop/api-client-react";
import { IconMobile, IconPlus, IconUp, IconX, IconWebsite, IconDesign, IconChart, IconSlides, IconArrowLeft, IconArrowRight } from "../layout/Icons";
import {
  MAX_ATTACHMENTS,
  Thumbnail,
  extractPastedImages,
  fileToAttachment,
  hasDraggedFiles,
  type Attachment,
} from "../chat/attachments";

const TYPES: ReadonlyArray<{
  type: string;
  label: string;
  icon: React.ReactNode;
  /** Map to the closest underlying ProjectType for the API. */
  apiType: ProjectType;
}> = [
  { type: "web", label: "Website", icon: <IconWebsite />, apiType: "web" },
  { type: "mobile", label: "Mobile", icon: <IconMobile size={18} />, apiType: "mobile" },
  { type: "design", label: "Design", icon: <IconDesign />, apiType: "component" },
  { type: "slides", label: "Slides", icon: <IconSlides />, apiType: "web" },
  { type: "animation", label: "Animation", icon: <IconChart />, apiType: "web" },
  { type: "data-viz", label: "Data Visualization", icon: <IconChart />, apiType: "backend" },
  { type: "3d-game", label: "3D Game", icon: <IconDesign />, apiType: "web" },
  { type: "document", label: "Document", icon: <IconWebsite />, apiType: "web" },
  { type: "spreadsheet", label: "Spreadsheet", icon: <IconChart />, apiType: "backend" },
];

export interface PromptComposerProps {
  prompt: string;
  onPromptChange: (value: string) => void;
  selectedType: ProjectType;
  onTypeChange: (type: ProjectType) => void;
  planMode: boolean;
  onPlanModeChange: (v: boolean) => void;
  onSubmit: () => void;
  pending?: boolean;
  errorMessage?: string;
  attachments: Attachment[];
  onAttachmentsChange: (next: Attachment[]) => void;
}

export function PromptComposer(props: PromptComposerProps) {
  const [activeChip, setActiveChip] = useState<string>("mobile");
  const [dragging, setDragging] = useState(false);
  const [pickingFiles, setPickingFiles] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handlePick = (entry: (typeof TYPES)[number]) => {
    setActiveChip(entry.type);
    props.onTypeChange(entry.apiType);
  };

  async function addFiles(files: File[]) {
    // Permissive filter: macOS often reports image/heic, image/jpeg, image/*,
    // or even empty string for some screenshots. Trust the picker's accept=
    // attribute and just include anything except clearly-non-image MIMEs.
    const candidates = files.filter(
      (f) => !f.type || f.type === "" || f.type.startsWith("image/"),
    );
    if (candidates.length === 0) return;
    setPickingFiles(true);
    try {
      const room = MAX_ATTACHMENTS - props.attachments.length;
      const slice = candidates.slice(0, Math.max(0, room));
      // allSettled so one unreadable file (e.g. HEIC in browsers that
      // can't decode it) doesn't drop the others.
      const settled = await Promise.allSettled(slice.map(fileToAttachment));
      const added: Attachment[] = [];
      settled.forEach((r, i) => {
        if (r.status === "fulfilled") added.push(r.value);
        else
          console.warn(
            `Failed to attach ${slice[i]?.name ?? "image"}:`,
            r.reason,
          );
      });
      if (added.length > 0)
        props.onAttachmentsChange(
          [...props.attachments, ...added].slice(0, MAX_ATTACHMENTS),
        );
    } finally {
      setPickingFiles(false);
    }
  }

  const atCap = props.attachments.length >= MAX_ATTACHMENTS;
  const sendDisabled = !props.prompt.trim() || !!props.pending || pickingFiles;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 28, width: "100%", maxWidth: 720 }}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!sendDisabled) props.onSubmit();
        }}
        onDragEnter={(e) => {
          if (hasDraggedFiles(e.dataTransfer.types)) {
            e.preventDefault();
            setDragging(true);
          }
        }}
        onDragOver={(e) => {
          if (hasDraggedFiles(e.dataTransfer.types)) {
            e.preventDefault();
            e.dataTransfer.dropEffect = "copy";
          }
        }}
        onDragLeave={(e) => {
          if (e.currentTarget === e.target) setDragging(false);
        }}
        onDrop={(e) => {
          if (!hasDraggedFiles(e.dataTransfer.types)) return;
          e.preventDefault();
          setDragging(false);
          void addFiles(Array.from(e.dataTransfer.files));
        }}
        style={{ position: "relative" }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: "none" }}
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            e.target.value = "";
            if (files.length > 0) void addFiles(files);
          }}
        />
        <div
          style={{
            border: `1px solid ${dragging ? "var(--accent-blue)" : "var(--border-default)"}`,
            borderRadius: 14,
            background: "var(--bg-panel)",
            padding: 14,
            display: "flex",
            flexDirection: "column",
            gap: 12,
            boxShadow: "0 1px 0 rgba(255,255,255,0.02) inset",
            transition: "border-color 120ms",
          }}
        >
          {props.attachments.length > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {props.attachments.map((a) => (
                <Thumbnail
                  key={a.id}
                  att={a}
                  onRemove={() =>
                    props.onAttachmentsChange(
                      props.attachments.filter((x) => x.id !== a.id),
                    )
                  }
                />
              ))}
              {pickingFiles && (
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 8,
                    border: "1px dashed var(--border-default)",
                    display: "grid",
                    placeItems: "center",
                    color: "var(--text-tertiary)",
                    fontSize: 10,
                  }}
                >
                  …
                </div>
              )}
            </div>
          )}
          <textarea
            value={props.prompt}
            onChange={(e) => props.onPromptChange(e.target.value)}
            onPaste={(e) => {
              const files = extractPastedImages(e.clipboardData.items);
              if (files.length > 0) {
                e.preventDefault();
                void addFiles(files);
              }
            }}
            placeholder={
              props.attachments.length > 0
                ? "Describe what to do with these images…"
                : "please create the clone of netflix mobile app"
            }
            rows={2}
            style={{
              width: "100%",
              border: "none",
              outline: "none",
              background: "transparent",
              color: "var(--text-primary)",
              fontSize: 14,
              fontFamily: "var(--font-sans)",
              resize: "none",
              minHeight: 42,
            }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={atCap || pickingFiles}
              title={atCap ? `Max ${MAX_ATTACHMENTS} attachments` : "Attach images"}
              aria-label="Attach images"
              style={{
                ...iconButton,
                cursor: atCap || pickingFiles ? "not-allowed" : "pointer",
                opacity: atCap ? 0.5 : 1,
              }}
            >
              <IconPlus />
            </button>
            {props.attachments.length > 0 && (
              <span
                style={{
                  fontSize: 11,
                  color: "var(--text-tertiary)",
                  fontVariantNumeric: "tabular-nums",
                }}
                title={`${props.attachments.length} of ${MAX_ATTACHMENTS} attached`}
              >
                {props.attachments.length}/{MAX_ATTACHMENTS}
              </span>
            )}
            <span style={typeChip}>
              <IconMobile size={12} />
              Mobile
              <span style={{ display: "inline-flex", color: "var(--text-tertiary)", cursor: "pointer" }}>
                <IconX />
              </span>
            </span>
            <div style={{ flex: 1 }} />
            <label style={planToggle}>
              <input
                type="checkbox"
                checked={props.planMode}
                onChange={(e) => props.onPlanModeChange(e.target.checked)}
                style={{ accentColor: "var(--accent-blue)" }}
              />
              Plan
            </label>
            <button
              type="submit"
              disabled={sendDisabled}
              style={{
                ...sendButton,
                background: sendDisabled ? "var(--bg-active)" : "var(--accent-blue)",
                color: sendDisabled ? "var(--text-tertiary)" : "#fff",
                cursor: sendDisabled ? "not-allowed" : "pointer",
              }}
              aria-label="Submit"
            >
              <IconUp />
            </button>
          </div>
        </div>
        {dragging && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(99,102,241,0.08)",
              border: "1px dashed var(--accent-blue)",
              borderRadius: 14,
              display: "grid",
              placeItems: "center",
              color: "var(--accent-blue)",
              fontSize: 13,
              fontWeight: 500,
              pointerEvents: "none",
            }}
          >
            Drop images to attach
          </div>
        )}
      </form>

      {props.errorMessage && (
        <div
          style={{
            padding: 10,
            background: "rgba(239,68,68,0.08)",
            border: "1px solid rgba(239,68,68,0.4)",
            borderRadius: 8,
            color: "var(--accent-red)",
            fontSize: 12,
          }}
        >
          {props.errorMessage}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
        <button type="button" style={arrowBtn} aria-label="Scroll left">
          <IconArrowLeft />
        </button>
        {TYPES.map((entry) => {
          const active = entry.type === activeChip;
          return (
            <button
              key={entry.type}
              type="button"
              onClick={() => handlePick(entry)}
              style={{
                display: "inline-flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 6,
                padding: "10px 16px",
                background: active ? "var(--bg-active)" : "transparent",
                border: `1px solid ${active ? "var(--border-strong)" : "transparent"}`,
                borderRadius: 10,
                color: active ? "var(--text-primary)" : "var(--text-secondary)",
                fontSize: 12,
                minWidth: 88,
                cursor: "pointer",
              }}
            >
              {entry.icon}
              {entry.label}
            </button>
          );
        })}
        <button type="button" style={arrowBtn} aria-label="Scroll right">
          <IconArrowRight />
        </button>
      </div>
    </div>
  );
}

const iconButton: React.CSSProperties = {
  width: 28,
  height: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "transparent",
  border: "1px solid var(--border-default)",
  borderRadius: 7,
  color: "var(--text-secondary)",
  cursor: "pointer",
};

const typeChip: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "5px 10px",
  border: "1px solid var(--border-default)",
  borderRadius: 16,
  background: "var(--bg-panel-2)",
  color: "var(--text-secondary)",
  fontSize: 12,
};

const planToggle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  fontSize: 12,
  color: "var(--text-secondary)",
  cursor: "pointer",
};

const sendButton: React.CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 8,
  border: "none",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const arrowBtn: React.CSSProperties = {
  width: 28,
  height: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "transparent",
  border: "none",
  color: "var(--text-tertiary)",
  cursor: "pointer",
};
